// @ts-strict

import { ChangeDetectorRef, Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { CampaignConfigurationModalTypes, ISingleCampaign } from '@app/core/models/core-models';
import { IvrConfigurationsService } from '@app/core/services/ivr-configurations/ivr-configurations.service';
import { PpiService } from '@app/core/services/ppi/ppi.service';
import { UrlAddressConfigService } from '@services/url-address-config/url-address-config.service';
import { GlobalCheckboxComponent } from '@app/shared/components/global-checkbox/global-checkbox.component';
import { TextInputComponent, TextInputData } from '@app/shared/components/modal-inputs/text-input/text-input.component';
import { TextareaInputComponent, TextareaInputValueEmitter } from '@app/shared/components/modal-inputs/textarea-input/textarea-input.component';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { RadioInputValue } from '../../modal-inputs/radio-input/radio-input.component';
import { SelectValue } from '../../modal-inputs/select-input/select-input.component';
import { IvrPpiModalContainerComponent } from '../ivr-ppi-modal-container.component';
import { SelectConfigInputValue } from './select-configuration-container/select-configuration-input/select-configuration-input.component';
import { CreatedConfigurationData, ICampaignFieldData, IPPIImgFieldValue } from '@services/url-address-config/url-address-config.types';
import { CampaignConfigurationsService } from '@pages/campaigns/campaign-details-view/campaign-configurations/campaign-configurations.service';
import { CollectActivityService } from '@services/collect-activity/collect-activity.service';
import { NotificationType } from '@app/core/services/global-notifications/global-notifications.model';

@Component({
  selector: 'app-create-configuration',
  templateUrl: './create-configuration.component.html',
  styleUrls: ['./create-configuration.component.scss']
})
export class CreateConfigurationComponent implements OnInit {
  @Input() currentCampaign?: ISingleCampaign;
  @Input() canCreateNumberConfig = false;
  @Input() configurationData?: any;
  @Input() modalType?: CampaignConfigurationModalTypes;
  @Input() duplicateModal?: string;
  @ViewChild(GlobalCheckboxComponent) checkboxComponent?: GlobalCheckboxComponent;
  @ViewChildren(TextInputComponent) textInputList?: QueryList<TextInputComponent>;
  @ViewChildren(TextareaInputComponent) textareaInputList?: QueryList<TextareaInputComponent>;
  modalFields: ICampaignFieldData[] = [];
  submitted = false;
  fieldsToSend: Record<string, string | number> = {};
  showEditButton = false;
  isLoaded = false;
  checkboxAccepted = false;
  acceptRegulations: UntypedFormGroup;
  selectedNumber?: string;
  selectedInternal?: number;
  configFieldIndex = 0;
  panelLanguage = '';
  downloadButtonImageID = 0;

  constructor(
    private globalNotificationsService: GlobalNotificationsService,
    public activeModal: NgbActiveModal,
    private ivrService: IvrConfigurationsService,
    private configService: UrlAddressConfigService,
    private fb: UntypedFormBuilder,
    private modalService: NgbModal,
    private translate: TranslateService,
    private ppiService: PpiService,
    private readonly cdr: ChangeDetectorRef,
    private readonly campaignConfigurationsService: CampaignConfigurationsService,
    private readonly collectActivity: CollectActivityService
  ) {
    this.acceptRegulations = this.fb.group({
      acceptTerms: ['', [Validators.required]]
    });
  }

  get isAddModal(): boolean {
    return this.modalType === 'create_configuration' || this.modalType === 'create_direct_link';
  }
  get directLinksConfig(): boolean {
    return this.modalType === 'create_direct_link' || this.modalType === 'edit_link_configuration';
  }
  get isPromotionPending(): boolean {
    return this.ivrService.isPromotionPending;
  }
  get isCreatePPIConfig(): boolean {
    return this.modalFields[this.configFieldIndex]?.system_name === 'ppi';
  }

  get isEditConfiguration(): boolean {
    return this.modalType === 'edit_configuration';
  }

  ngOnInit(): void {
    this.panelLanguage = this.translate.currentLang;
    if (this.isAddModal) {
      this.initializeAddModeData();
    } else {
      this.initializeEditModeData();
    }
  }

  get form() {
    return this.acceptRegulations.controls;
  }

  private initializeAddModeData() {
    if (this.currentCampaign?.id) {
      this.configService.getModalConfig(this.currentCampaign.id).subscribe(response => {
        this.modalFields = response.data?.fields || [];
        this.isLoaded = true;
        this.modalFields.map(field => {
          if (field.system_name) {
            this.fieldsToSend[field.system_name] = '';
          }
        });
        const downloadImgField = this.modalFields.find(field => field.system_name === 'download_img');
        if (downloadImgField) {
          this.fieldsToSend.download_img = (downloadImgField.values as IPPIImgFieldValue[])[0]?.id || 0;
        }
        this.configFieldIndex = this.modalFields.findIndex(field => field.system_name === 'phone' || field.system_name === 'ppi');
      });
    }
  }

  private initializeEditModeData() {
    if (this.currentCampaign?.id) {
      this.configService.getEditConfigData(this.currentCampaign.id, this.configurationData.id).subscribe(response => {
        this.modalFields = response.data?.fields as ICampaignFieldData[];
        this.isLoaded = true;

        if (this.modalFields.find(({ system_name, config_values }) => system_name === 'fileimg' && !config_values)) {
          this.fieldsToSend = { ...this.fieldsToSend, fileimg: '' };
        }

        const downloadImgField = this.modalFields.find(field => field.system_name === 'download_img');
        if (downloadImgField) {
          const idOfBanner = parseInt(downloadImgField.config_values || '0', 10);
          const bannerValue = (downloadImgField.values as IPPIImgFieldValue[]).find(value => value.id === idOfBanner);

          if (bannerValue) {
            this.fieldsToSend.download_img = bannerValue?.id || 0;
            this.downloadButtonImageID = (downloadImgField.values as IPPIImgFieldValue[]).indexOf(bannerValue);
          }
        }
      });
    }
  }

  getValue(event: SelectValue | TextareaInputValueEmitter | TextInputData | RadioInputValue | SelectConfigInputValue): void {
    if (event.system) {
      this.fieldsToSend[event.system] = event.value || '';

      if (event.system === 'download_img' && event.value === 0) {
        this.fieldsToSend[event.system] = event.value;
      }

      if (!this.isAddModal) {
        const dataToCompare = this.modalFields.find(config => config.system_name === event.system);

        if (dataToCompare?.config_values && dataToCompare.config_values.toString() !== event.value.toString()) {
          this.showEditButton = true;
          this.cdr.detectChanges();
        }
      }
    }

    if ('number' in event && 'number_internal' in event) {
      this.selectedNumber = event.number;
      this.selectedInternal = event.number_internal;
    }
  }

  verifyForm(): void {
    this.submitted = true;
    let invalid = false;
    this.textInputList?.map(singleInput => {
      if (singleInput.configForm.text.invalid) {
        invalid = true;
        return;
      }
    });
    this.textareaInputList?.map(singleInput => {
      if (singleInput.configForm.textarea.invalid) {
        invalid = true;
        return;
      }
    });
    if (this.checkboxComponent) {
      if ((this.isAddModal || this.duplicateModal) && !this.checkboxComponent?.checkboxAccepted) {
        return;
      }
    }
    if ((this.directLinksConfig || this.fieldsToSend.phone || this.fieldsToSend.ppi) && (this.fieldsToSend.title || this.fieldsToSend.Title) && !invalid) {
      if (this.isAddModal || this.duplicateModal) {
        this.addConfig();
      } else {
        this.editConfig();
      }
    } else if (this.fieldsToSend.title && !invalid && this.isEditConfiguration) {
      this.editConfig();
    } else if (this.isAddModal && this.fieldsToSend.title && !invalid && this.fieldsToSend.domain) {
      this.addConfig();
    } else {
      this.showNotification('view_translations.error_occured', NotificationTypes[1]);
    }
  }

  addConfig(): void {
    this.isLoaded = false;
    if (this.currentCampaign?.id) {
      this.configService.addConfiguration(this.fieldsToSend, this.currentCampaign.id).subscribe(
        res => {
          const config: Record<string, any> = { ...(res.data?.configuration || {}) };
          config.income = 0;
          config.currency = 'PLN';
          config.views = 0;
          if (!this.directLinksConfig && this.currentCampaign!.categories) {
            config.type = this.currentCampaign!.categories[0]?.id === 2 ? 1 : 2;
          }
          if (res.status_code === 200) {
            this.collectActivity.checkMeThenSendFingerprint();
            if (this.directLinksConfig) {
              this.ppiService.directLinkAddedOrEdited.next(config);
            } else {
              this.configService.addedEditedConfig.next({ name: this.currentCampaign!.name, config, id: this.currentCampaign!.id, mode: 'add' });
            }
            this.showNotification('view_translations.added_successfully', NotificationTypes[0]);
            this.campaignConfigurationsService.directlinkAdded.emit();
            this.activeModal.close();
          } else {
            this.showNotification('view_translations.error_occured', NotificationTypes[1]);
          }
        },
        error => {
          this.showNotification('view_translations.error_occured', NotificationTypes[1]);
          this.isLoaded = true;
        }
      );
    }
  }

  editConfig(): void {
    this.isLoaded = false;
    this.fieldsToSend.config_id = this.configurationData.id;

    if (this.currentCampaign?.id) {
      this.configService.editCurrentConfig(this.currentCampaign.id, this.fieldsToSend).subscribe(
        res => {
          const config: CreatedConfigurationData & Record<string, any> = { ...(res.data?.configuration || {}) };
          config.earned = '0 PLN';
          config.number = this.selectedNumber;
          config.number_internal = this.selectedInternal;
          config.rate = this.currentCampaign!.rate;
          config.income = this.currentCampaign!.income || 0;
          config.currency = this.currentCampaign!.currency || 'PLN';
          config.views = this.currentCampaign!.views || 0;
          config.earn = this.currentCampaign!.earn || 0;
          if (this.currentCampaign!.categories) {
            config.type = this.currentCampaign!.categories[0].id === 2 ? 1 : 2;
          }
          if (res.status_code === 200) {
            if (this.directLinksConfig) {
              this.ppiService.directLinkAddedOrEdited.next(config);
            } else {
              this.configService.addedEditedConfig.next({ name: this.currentCampaign!.name, config, id: this.currentCampaign!.id, mode: 'edit' });
            }
            this.showNotification('view_translations.edited_successfully', NotificationTypes[0]);
            this.activeModal.close();
          } else {
            this.showNotification('view_translations.error_occured', NotificationTypes[1]);
          }
        },
        error => {
          this.showNotification('view_translations.error_occured', NotificationTypes[1]);
          this.isLoaded = true;
        }
      );
    }
  }

  checkValidators(systemName: string): Array<string> {
    const validators: string[] = [];

    switch (systemName.toLowerCase()) {
      case 'fbimg':
      case 'image':
        validators.push('url');
        break;
      case 'age':
        validators.push('age');
        break;
      case 'name':
        validators.push('name');
        break;
      case 'title':
      case 'filename':
        validators.push('required');
        break;
    }
    return validators;
  }

  showNotification(message: string, type: NotificationType): void {
    this.globalNotificationsService.show({
      message,
      type,
      translate: true,
      isPersistent: false,
      id: ''
    });
  }

  open(modalDescription: string, modalTitle: string, modalType: CampaignConfigurationModalTypes, configData?: undefined): void {
    if (modalType !== 'choose_button') {
      this.activeModal.close();
    }
    const modalRef = this.modalService.open(IvrPpiModalContainerComponent, {
      size: 'me'
    });

    const component = modalRef.componentInstance as IvrPpiModalContainerComponent;

    component.modalDescriptionName = modalDescription;
    component.modalTitle = modalTitle;
    component.modalType = modalType;
    component.configurationData = configData;
    if (modalType === 'choose_button') {
      modalRef.result.then(res => {
        if (res) {
          this.getValue({ value: res.id, system: 'download_img' });
          const ppiImgFields = this.modalFields.find(field => field.type === 'ppi_img')?.values;
          const currentField = ((ppiImgFields as IPPIImgFieldValue[]) || []).find(elem => elem.id === res.id);
          if (currentField) {
            this.downloadButtonImageID = ((ppiImgFields as IPPIImgFieldValue[]) || []).indexOf(currentField);
          }
        }
      });
    }
    if (this.isCreatePPIConfig) {
      component.campaignData = { id: 6646 };
    }
  }

  public handleRegulations(): void {
    this.ivrService.getRegulations().subscribe(data => {
      const downloadURL = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = 'regulaminivr.pdf';
      link.click();
      link.remove();
    });
  }
}
